import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';

interface NotionSetup {
  database?: string;
  trigger?: string;
  extra_trigger?: string;
  action?: string;
}

interface Automation {
  slug: string;
  name: string;
  description: string;
  notion_setup: NotionSetup;
  enabled: boolean;
  webhook_path?: string;
  secret_revealed?: boolean;
  required_header_name?: string | null;
  has_required_header?: boolean;
  settings: Record<string, any>;
  default_settings: Record<string, any>;
  total_runs: number;
  last_run_at: string | null;
  last_run_status: string | null;
  notion_configured: boolean;
}

interface Run {
  id: number;
  slug: string;
  status: string;
  source: string;
  page_id: string | null;
  page_title: string | null;
  message: string | null;
  tickets_updated: number;
  duration_ms: number | null;
  steps: string[];
  payload: any;
  created_at: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  skipped: 'bg-gray-100 text-gray-700 border-gray-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  rejected: 'bg-amber-100 text-amber-800 border-amber-200',
};

const SETTING_LABELS: Record<string, string> = {
  sync_client: 'Copy Client',
  sync_assignee: 'Copy Assignee',
  retry_attempts: 'Read attempts',
  retry_delay_seconds: 'Delay between attempts (s)',
  overwrite_existing: 'Overwrite a value already on the ticket',
  task_tickets_property: 'Task → tickets relation',
  task_client_property: 'Task → client relation',
  ticket_clients_property: 'Ticket → clients relation',
  task_assignee_property: 'Task → assignee people',
  ticket_assignee_property: 'Ticket → assignee people',
};

const SETTING_HELP: Record<string, string> = {
  sync_client: "The task's Client relation → the ticket's Clients relation.",
  sync_assignee: "The task's Assignee people → the ticket's Assignee people.",
  retry_attempts:
    'Notion fires the webhook at the same moment it writes the property, so the first read can come back empty. Each extra attempt re-reads the page.',
  overwrite_existing:
    'Off means a ticket field that already holds something different is left untouched and reported instead. An empty field on the task is never copied either way — clearing Client on a task will not wipe it off the tickets.',
};

const NUMBER_SETTINGS = new Set(['retry_attempts', 'retry_delay_seconds']);
const ADVANCED_SETTINGS = new Set([
  'task_tickets_property',
  'task_client_property',
  'ticket_clients_property',
  'task_assignee_property',
  'ticket_assignee_property',
]);

const formatWhen = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const NotionApiAutomationPanel: React.FC = () => {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [runs, setRuns] = useState<Record<string, Run[]>>({});
  const [expandedRun, setExpandedRun] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string>('');

  const [testPage, setTestPage] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<Record<string, any>>({});

  const loadRuns = useCallback(async (slug: string) => {
    try {
      const r = await api.get(`/notion-automation/automations/${slug}/runs?limit=25`);
      setRuns((prev) => ({ ...prev, [slug]: r.data?.runs || [] }));
    } catch {
      /* run log is non-critical */
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/notion-automation/automations');
      const list: Automation[] = r.data?.automations || [];
      setAutomations(list);
      await Promise.all(list.map((a) => loadRuns(a.slug)));
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Could not load automations');
    } finally {
      setLoading(false);
    }
  }, [loadRuns]);

  useEffect(() => { load(); }, [load]);

  const replace = (updated: Automation) =>
    setAutomations((prev) => prev.map((a) => (a.slug === updated.slug ? updated : a)));

  const patch = async (slug: string, body: Record<string, any>) => {
    setBusy(slug);
    try {
      const r = await api.patch(`/notion-automation/automations/${slug}`, body);
      replace(r.data);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Update failed');
    } finally {
      setBusy('');
    }
  };

  const reveal = async (slug: string) => {
    setBusy(slug);
    try {
      const r = await api.get(`/notion-automation/automations/${slug}/secret`);
      replace(r.data);
    } catch (e: any) {
      setError(e?.message || 'Could not reveal the webhook URL');
    } finally {
      setBusy('');
    }
  };

  const rotate = async (slug: string) => {
    if (!window.confirm(
      'Rotate the webhook secret?\n\nThe current URL stops working immediately — you will have to paste the new one into the Notion automation.'
    )) return;
    setBusy(slug);
    try {
      const r = await api.post(`/notion-automation/automations/${slug}/rotate`);
      replace(r.data);
      setNotice('New webhook URL generated — update it in Notion.');
    } catch (e: any) {
      setError(e?.message || 'Rotate failed');
    } finally {
      setBusy('');
    }
  };

  const runTest = async (slug: string, apply: boolean) => {
    const page = (testPage[slug] || '').trim();
    if (!page) { setError('Paste a Notion task URL or page id to test against.'); return; }
    if (apply && !window.confirm('Run for real? This writes to Notion.')) return;
    setBusy(`${slug}-test`);
    setTestResult((p) => ({ ...p, [slug]: null }));
    try {
      const r = await api.post(`/notion-automation/automations/${slug}/test`, { page, apply });
      setTestResult((p) => ({ ...p, [slug]: r.data }));
      setError('');
      await Promise.all([loadRuns(slug), load()]);
    } catch (e: any) {
      setError(e?.message || 'Test failed');
    } finally {
      setBusy('');
    }
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    setNotice('Webhook URL copied.');
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-500">
        Loading automations…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Notion API Automation</h2>
        <p className="text-sm text-gray-500 mt-1">
          Notion database automations post to a private BNSystem webhook, and BNSystem writes back
          through the Notion API. Replaces the equivalent Make.com scenarios.
        </p>
      </div>

      {notice && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="opacity-50 hover:opacity-100">&times;</button>
        </div>
      )}
      {error && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          <span>{error}</span>
          <button onClick={() => setError('')} className="opacity-50 hover:opacity-100">&times;</button>
        </div>
      )}

      {automations.length > 0 && !automations[0].notion_configured && (
        <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <strong>NOTION_API_KEY is not set on this server.</strong> Webhooks will be accepted and
          logged, but no Notion writes can happen until the key is configured.
        </div>
      )}

      {automations.map((a) => {
        const fullUrl = `${window.location.origin}${a.webhook_path || ''}`;
        const slugRuns = runs[a.slug] || [];
        const result = testResult[a.slug];
        const advanced = !!showAdvanced[a.slug];

        return (
          <div key={a.slug} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900 truncate">{a.name}</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                    a.enabled
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-gray-100 text-gray-600 border-gray-200'
                  }`}>
                    {a.enabled ? 'Live' : 'Off'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{a.description}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {a.total_runs} run{a.total_runs === 1 ? '' : 's'}
                  {a.last_run_at && <> · last {formatWhen(a.last_run_at)} ({a.last_run_status})</>}
                </p>
              </div>
              <button
                onClick={() => patch(a.slug, { enabled: !a.enabled })}
                disabled={busy === a.slug}
                className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  a.enabled ? 'bg-emerald-600' : 'bg-gray-300'
                }`}
                aria-label={a.enabled ? 'Disable automation' : 'Enable automation'}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  a.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* Webhook */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Webhook URL
                </label>
                <div className="flex items-center gap-3 text-xs">
                  {!a.secret_revealed ? (
                    <button onClick={() => reveal(a.slug)} disabled={busy === a.slug}
                      className="text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50">
                      Reveal
                    </button>
                  ) : (
                    <button onClick={() => copy(fullUrl)}
                      className="text-emerald-700 hover:text-emerald-800 font-medium">
                      Copy
                    </button>
                  )}
                  <button onClick={() => rotate(a.slug)} disabled={busy === a.slug}
                    className="text-gray-500 hover:text-red-700 font-medium disabled:opacity-50">
                    Rotate
                  </button>
                </div>
              </div>
              <code className="block w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-800 font-mono break-all">
                {fullUrl}
              </code>
              <p className="text-xs text-gray-400 mt-1.5">
                The secret in the path is the only credential — treat it like a password. Anyone who
                has it can trigger this automation. Rotate it if it leaks.
              </p>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Extra header name <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    defaultValue={a.required_header_name || ''}
                    placeholder="X-BN-Signature"
                    onBlur={(e) => {
                      if (e.target.value.trim() !== (a.required_header_name || '')) {
                        patch(a.slug, { required_header_name: e.target.value });
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Extra header value {a.has_required_header && <span className="text-emerald-600">· set</span>}
                  </label>
                  <input
                    type="password"
                    placeholder={a.has_required_header ? '•••••••• (unchanged)' : 'shared secret'}
                    onBlur={(e) => {
                      if (e.target.value) {
                        patch(a.slug, { required_header_value: e.target.value });
                        e.target.value = '';
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Set both to require a matching header on every delivery — a second lock in case the
                URL is ever exposed. Add it under "Add custom header" in the Notion automation.
              </p>
            </div>

            {/* Notion setup */}
            {a.notion_setup?.trigger && (
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  Set up in Notion
                </div>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Open the <strong>{a.notion_setup.database}</strong> database → Automations → new automation.</li>
                  <li>Trigger: <strong>{a.notion_setup.trigger}</strong>.</li>
                  {a.notion_setup.extra_trigger && (
                    <li>{a.notion_setup.extra_trigger}.</li>
                  )}
                  <li>Action: <strong>{a.notion_setup.action}</strong>, pasting the webhook URL above.</li>
                  <li>Turn this automation on here, then edit one task's Client or Assignee to test.</li>
                </ol>
              </div>
            )}

            {/* Settings */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Behaviour</span>
                <button onClick={() => setShowAdvanced((p) => ({ ...p, [a.slug]: !advanced }))}
                  className="text-xs text-gray-500 hover:text-gray-800">
                  {advanced ? 'Hide property names' : 'Property names'}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(a.settings)
                  .filter(([k]) => advanced || !ADVANCED_SETTINGS.has(k))
                  .map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-600 mb-1">
                        {SETTING_LABELS[key] || key}
                      </label>
                      {typeof value === 'boolean' ? (
                        <button
                          onClick={() => patch(a.slug, { settings: { [key]: !value } })}
                          disabled={busy === a.slug}
                          className={`px-3 py-1.5 text-sm rounded border transition-colors disabled:opacity-50 ${
                            value
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                              : 'bg-gray-50 border-gray-200 text-gray-600'
                          }`}
                        >
                          {value ? 'Yes' : 'No'}
                        </button>
                      ) : (
                        <input
                          type={NUMBER_SETTINGS.has(key) ? 'number' : 'text'}
                          defaultValue={String(value ?? '')}
                          min={NUMBER_SETTINGS.has(key) ? 0 : undefined}
                          onBlur={(e) => {
                            const raw = e.target.value;
                            const next = NUMBER_SETTINGS.has(key) ? Number(raw) : raw;
                            if (String(next) !== String(value)) {
                              patch(a.slug, { settings: { [key]: next } });
                            }
                          }}
                          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      )}
                      {SETTING_HELP[key] && (
                        <p className="text-xs text-gray-400 mt-1">{SETTING_HELP[key]}</p>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Test */}
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Test against one page
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={testPage[a.slug] || ''}
                  onChange={(e) => setTestPage((p) => ({ ...p, [a.slug]: e.target.value }))}
                  placeholder="Paste a Notion task URL or page id"
                  className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  onClick={() => runTest(a.slug, false)}
                  disabled={busy === `${a.slug}-test`}
                  className="px-4 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                >
                  {busy === `${a.slug}-test` ? 'Running…' : 'Dry run'}
                </button>
                <button
                  onClick={() => runTest(a.slug, true)}
                  disabled={busy === `${a.slug}-test`}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
                >
                  Run for real
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                A dry run reads Notion and reports exactly what it would change without writing.
              </p>

              {result && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                      STATUS_STYLES[result.result?.status] || STATUS_STYLES.skipped
                    }`}>
                      {result.result?.status}
                    </span>
                    {result.dry_run && (
                      <span className="text-xs text-gray-500">dry run — nothing was written</span>
                    )}
                    <span className="text-sm text-gray-700">{result.result?.message}</span>
                  </div>
                  {(result.steps || []).length > 0 && (
                    <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap">
                      {(result.steps as string[]).join('\n')}
                    </pre>
                  )}
                </div>
              )}
            </div>

            {/* Run log */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Recent deliveries
                </span>
                <button onClick={() => loadRuns(a.slug)} className="text-xs text-gray-500 hover:text-gray-800">
                  Refresh
                </button>
              </div>

              {slugRuns.length === 0 ? (
                <p className="text-sm text-gray-400 py-4 text-center">
                  Nothing yet. Deliveries appear here the moment Notion posts to the webhook.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-3 font-medium">When</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium">Task</th>
                        <th className="py-2 pr-3 font-medium">Result</th>
                        <th className="py-2 pr-3 font-medium text-right">Took</th>
                        <th className="py-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {slugRuns.map((run) => (
                        <React.Fragment key={run.id}>
                          <tr className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                              {formatWhen(run.created_at)}
                              {run.source === 'test' && (
                                <span className="ml-1.5 text-xs text-gray-400">test</span>
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                                STATUS_STYLES[run.status] || STATUS_STYLES.skipped
                              }`}>
                                {run.status}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-gray-800 max-w-xs truncate" title={run.page_title || ''}>
                              {run.page_title || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="py-2 pr-3 text-gray-600">{run.message || '—'}</td>
                            <td className="py-2 pr-3 text-gray-400 text-right whitespace-nowrap">
                              {run.duration_ms != null ? `${run.duration_ms} ms` : '—'}
                            </td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                                className="text-xs text-gray-500 hover:text-gray-800"
                              >
                                {expandedRun === run.id ? 'Hide' : 'Details'}
                              </button>
                            </td>
                          </tr>
                          {expandedRun === run.id && (
                            <tr className="bg-gray-50">
                              <td colSpan={6} className="px-3 py-3">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-xs font-semibold text-gray-600 mb-1">Steps</div>
                                    <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap bg-white p-2 rounded border border-gray-200 max-h-64 overflow-auto">
                                      {(run.steps || []).join('\n') || '(none)'}
                                    </pre>
                                  </div>
                                  <div>
                                    <div className="text-xs font-semibold text-gray-600 mb-1">
                                      Payload Notion sent
                                    </div>
                                    <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap bg-white p-2 rounded border border-gray-200 max-h-64 overflow-auto">
                                      {JSON.stringify(run.payload ?? {}, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotionApiAutomationPanel;
